// src/components/common/AnimatedNumber.jsx
// Animated number component with smooth counting effect

import React, { useState, useEffect, useRef } from 'react';

const AnimatedNumber = ({ 
  value, 
  duration = 2000, 
  formatFunction, 
  delay = 0,
  style = {},
  className = ""
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  const animationRef = useRef(null);
  const hasAnimated = useRef(false);

  // Intersection Observer to trigger animation when element comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  // Animation logic
  useEffect(() => {
    if (!isVisible) return;

    const startTime = performance.now() + delay;
    const startValue = 0;
    const endValue = value || 0;
    const valueDifference = endValue - startValue;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      
      if (elapsed < 0) {
        // Still in delay period
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = startValue + (valueDifference * easeOut);
      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(endValue);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible, value, duration, delay]);

  // Format the display value
  const formattedValue = formatFunction 
    ? formatFunction(displayValue) 
    : Math.round(displayValue).toLocaleString();

  return (
    <span 
      ref={elementRef}
      style={style}
      className={className}
    >
      {formattedValue}
    </span>
  );
};

export default AnimatedNumber;